/**
 * Dominios conocidos, ordenados por peso real en la base (jul-2026): gmail 78.6%
 * y hotmail 12.6% ya cubren el P90; con los diez primeros vamos al 97.8%.
 *
 * NO es un whitelist: un dominio fuera de esta lista PASA (ver `checkEmailDomain`).
 * La lista existe para tener contra qué medir distancia de edición — restringir a
 * ella dejaría fuera el 2.2% de correos corporativos, .edu.mx y el relay de Apple,
 * que son usuarios reales.
 */
export const AUTHORIZED_EMAIL_DOMAINS = [
	'gmail.com',
	'googlemail.com',
	'hotmail.com',
	'hotmail.es',
	'hotmail.com.mx',
	'outlook.com',
	'outlook.es',
	'yahoo.com',
	'yahoo.com.mx',
	'yahoo.es',
	'icloud.com',
	'live.com',
	'live.com.mx',
	'proton.me',
	'protonmail.com',
	// Cola larga observada en la base que sí es legítima: sin ella, un `msn.com`
	// o un `me.com` quedan a distancia de edición de otro dominio y se marcan
	// como typo de algo que el usuario nunca escribió.
	'msn.com',
	'me.com',
	'mac.com',
	'gmx.com',
	'aol.com',
	'prodigy.net.mx',
	// Hide My Email de Apple: correo real y verificado, jamás un typo.
	'privaterelay.appleid.com',
] as const;

/**
 * Typos observados en la base que la distancia de edición NO alcanza: sufijos
 * completos de más de dos caracteres sobre un dominio válido (`gmail.com.mx` está
 * a distancia 3 de `gmail.com`, y la tolerancia máxima es 2). Son correos
 * PERMANENTEMENTE inalcanzables — el usuario nunca recibe declaraciones ni
 * recordatorios— así que se corrigen igual que un typo normal.
 */
const SUFFIX_TYPOS: readonly (readonly [string, string])[] = [
	['gmail.com.mx', 'gmail.com'],
	['hotmail.com.ar', 'hotmail.com'],
	['outlook.com.mx', 'outlook.com'],
];

/**
 * Normaliza un correo capturado: recorta y baja a minúsculas. Se aplica en la
 * CAPTURA (onChange), no en el submit — el teclado de móvil capitaliza la
 * primera letra por defecto y el usuario ni se entera de que escribió
 * `Rafael@gmail.com`, que nace como una cuenta DISTINTA de `rafael@gmail.com`.
 * UME #108 lo cerró del lado del servidor; esto evita que la mayúscula llegue
 * siquiera a verse. El complemento en el input es `autoCapitalize='none'`.
 */
export const normalizeEmail = (value: string): string => value.trim().toLowerCase();

export type EmailDomainStatus = 'authorized' | 'typo' | 'unknown' | 'invalid';

export type EmailDomainCheck = {
	status: EmailDomainStatus;
	suggestion?: string;
};

const editDistance = (a: string, b: string): number => {
	if (Math.abs(a.length - b.length) > 2) return 3;
	const previous = Array.from({ length: b.length + 1 }, (_, i) => i);
	for (let i = 1; i <= a.length; i++) {
		let diagonal = previous[0];
		previous[0] = i;
		for (let j = 1; j <= b.length; j++) {
			const substitution = diagonal + (a[i - 1] === b[j - 1] ? 0 : 1);
			diagonal = previous[j];
			previous[j] = Math.min(previous[j] + 1, previous[j - 1] + 1, substitution);
		}
	}
	return previous[b.length];
};

const typoToleranceFor = (domain: string) => (domain.length <= 8 ? 1 : 2);

const closestAuthorizedDomain = (domain: string): string | null => {
	let best: string | null = null;
	let bestDistance = Infinity;
	for (const candidate of AUTHORIZED_EMAIL_DOMAINS) {
		const distance = editDistance(domain, candidate);
		if (distance < bestDistance) {
			bestDistance = distance;
			best = candidate;
		}
	}
	return best && bestDistance <= typoToleranceFor(best) ? best : null;
};

export const checkEmailDomain = (email: string): EmailDomainCheck => {
	const atIndex = email.lastIndexOf('@');
	if (atIndex <= 0 || atIndex === email.length - 1) return { status: 'invalid' };

	const localPart = email.slice(0, atIndex);
	const domain = email.slice(atIndex + 1).toLowerCase();
	if (!domain.includes('.')) return { status: 'invalid' };

	if ((AUTHORIZED_EMAIL_DOMAINS as readonly string[]).includes(domain)) {
		return { status: 'authorized' };
	}

	const suffixTypo = SUFFIX_TYPOS.find(([wrong]) => wrong === domain);
	if (suffixTypo) {
		return { status: 'typo', suggestion: `${localPart}@${suffixTypo[1]}` };
	}

	const closest = closestAuthorizedDomain(domain);
	if (closest) {
		return { status: 'typo', suggestion: `${localPart}@${closest}` };
	}

	return { status: 'unknown' };
};
